// TitleScene.js（重构版 - 使用子模块）
import Phaser from 'phaser';
import SaveSystem from './modules/SaveSystem.js';
import SettingsHelper from './modules/SettingsHelper.js';
import DebugHelper from './modules/DebugHelper.js';
import TitleBackground from './modules/Title/TitleBackground.js';
import TitleMenu from './modules/Title/TitleMenu.js';
import TitleSettings from './modules/Title/TitleSettings.js';
import TitleInput from './modules/Title/TitleInput.js';
import { getResumeSceneKey } from './modules/SceneRegistry.js';
import GameStateStore from './modules/GameStateStore.js';

export default class TitleScene extends Phaser.Scene {
    constructor() {
        super('TitleScene');
    }
    
    init() {
        try {
            // 初始化标题场景
            this.isMenuOpen = false;
            this.menuActive = true;
            
            // 初始化子模块
            this.background = new TitleBackground(this);
            this.menu = new TitleMenu(this);
            this.settings = new TitleSettings(this);
            this.inputHandler = new TitleInput(this, this.menu);
            
            // 尝试获取gameCore和resourceManager，但不依赖它们
            try {
                this.gameCore = window.gameCore || this.game.gameCore;
                this.resourceManager = null;
                
                if (this.gameCore && this.gameCore.getSystem && typeof this.gameCore.getSystem === 'function') {
                    this.resourceManager = this.gameCore.getSystem('resource');
                }
            } catch (error) {
                // 获取资源管理器失败，但不影响游戏继续
            }
        } catch (error) {
            DebugHelper.error('TitleScene init error:', error);
        }
    }
    
    create() {
        try {
            // 创建背景
            this.background.create();
            
            // 创建主菜单
            this.menu.create();
            this.menu.setMenuItemClickCallback((index) => {
                this.handleMenuItemClick(index);
            });
            
            // 设置输入
            this.inputHandler.setupControls();
            
            // 添加版本信息
            this.addVersionInfo();
            
            // 尝试播放背景音乐（可选）
            const volume = SettingsHelper.getBGMPlayVolume();
            this.bgm = this.sound.add('start_bgm', { loop: true, volume: volume });
            this.bgm.play();
            
            // 设置BGM音量改变回调
            this.settings.setBGMVolumeChangeCallback((volume) => {
                if (this.bgm) {
                    this.bgm.setVolume(volume * 0.1); // 0.1是基础音量
                }
            });
        } catch (error) {
            DebugHelper.error('TitleScene create error:', error);
        }
    }
    
    addVersionInfo() {
        try {
            // 获取游戏版本，如果不存在则使用默认值
            let version = '1.0.0';
            try {
                if (this.game && this.game.registry && typeof this.game.registry.get === 'function') {
                    version = this.game.registry.get('gameVersion') || version;
                }
            } catch (error) {
                // 获取版本信息失败，使用默认版本
            }
            
            this.versionText = this.add.text(
                this.cameras.main.width - 10,
                this.cameras.main.height - 10,
                `v${version}`,
                {
                    fontSize: '12px',
                    fill: '#666666',
                    fontFamily: 'monospace'
                }
            );
            this.versionText.setOrigin(1, 1);
        } catch (error) {
            // 添加版本信息失败，跳过
        }
    }
    
    handleMenuItemClick(index) {
        try {
            switch (index) {
                case 0: // 单人游戏
                    this.startSinglePlayer();
                    break;
                case 1: // 多人游戏
                    this.startMultiplayer();
                    break;
                case 2: // 游戏设置
                    this.showSettings();
                    break;
                case 3: // 退出游戏
                    this.exitGame();
                    break;
            }
        } catch (error) {
            DebugHelper.error('handleMenuItemClick error:', error);
        }
    }
    
    async startSinglePlayer() {
        // 停止背景音乐
        this.stopBackgroundMusic();
        
        // 检查是否有存档（使用默认槽位0）
        const hasSave = await SaveSystem.hasSave(0);
        
        if (hasSave) {
            // 有存档，直接加载并进入游戏
            const saveData = await SaveSystem.load(0);
            if (saveData && saveData.gameState) {
                // 恢复游戏状态
                GameStateStore.replaceState(saveData.gameState);

                const targetScene = getResumeSceneKey(saveData.gameState);

                DebugHelper.log('从存档加载，跳转到场景:', targetScene);

                // 跳转到存档场景
                this.scene.start(targetScene);
            } else {
                const targetScene = getResumeSceneKey(GameStateStore.getState());
                this.scene.start(targetScene);
            }
        } else {
            const targetScene = getResumeSceneKey(GameStateStore.getState());
            this.scene.start(targetScene);
        }
    }
    
    startMultiplayer() {
        // 停止背景音乐
        this.stopBackgroundMusic();
        
        // 检查是否已登录
        if (window.userInfo && window.userInfo.userId) {
            // 已登录，直接进入多人游戏场景
            this.scene.start('MultiplayerScene', {
                userId: window.userInfo.userId,
                username: window.userInfo.username,
                playerData: window.userInfo.playerData
            });
        } else {
            // 未登录，跳转到登录场景
            this.scene.start('LoginScene');
        }
    }
    
    showSettings() {
        // 清除现有菜单
        this.menu.destroy();
        
        // 禁用菜单
        this.menuActive = false;
        
        // 显示设置界面
        this.settings.showSettings(() => {
            // 关闭设置后的回调
            this.menuActive = true;
            // 恢复主菜单（"游戏设置"是第3个菜单项，索引为2）
            this.menu.selectedMenuItem = 2;
            this.menu.create();
            this.menu.setMenuItemClickCallback((index) => {
                this.handleMenuItemClick(index);
            });
            this.menu.updateMenuSelection();
        });
    }
    
    exitGame() {
        // 退出游戏
        // 在网页版中，我们可以显示一个确认消息
        this.showMessage('确认退出游戏？(F5刷新页面)');
    }
    
    stopBackgroundMusic() {
        try {
            if (this.bgm && typeof this.bgm.stop === 'function') {
                this.bgm.stop();
            }
        } catch (error) {
            // 停止背景音乐时出错，但不影响游戏继续
        }
    }
    
    showMessage(message, onClose) {
        try {
            // 创建消息对话框 - 现代风格
            const dialogBg = this.add.graphics();
            dialogBg.fillStyle(0x1a1a2e, 0.95); // 深蓝色背景
            dialogBg.fillRoundedRect(
                this.cameras.main.centerX - 250,
                this.cameras.main.centerY - 100,
                500,
                200,
                15
            );
            
            // 对话框边框
            dialogBg.lineStyle(3, 0x4cc9f0, 1); // 青色边框
            dialogBg.strokeRoundedRect(
                this.cameras.main.centerX - 250,
                this.cameras.main.centerY - 100,
                500,
                200,
                15
            );
            
            const dialogText = this.add.text(
                this.cameras.main.centerX,
                this.cameras.main.centerY - 70,
                message,
                {
                    fontSize: '18px',
                    fill: '#e6e6e6',
                    fontFamily: 'monospace',
                    align: 'center',
                    lineSpacing: 8,
                    wordWrap: {
                        width: 450,
                        useAdvancedWrap: true
                    }
                }
            );
            dialogText.setOrigin(0.5);
            
            // 添加关闭按钮背景
            const buttonBg = this.add.graphics();
            buttonBg.fillStyle(0x4cc9f0, 1); // 青色背景
            buttonBg.fillRoundedRect(
                this.cameras.main.centerX - 40,
                this.cameras.main.centerY + 50,
                80,
                35,
                8
            );
            
            // 添加关闭按钮
            const closeButton = this.add.text(
                this.cameras.main.centerX,
                this.cameras.main.centerY + 67,
                '确定',
                {
                    fontSize: '16px',
                    fill: '#ffffff',
                    fontFamily: 'monospace',
                    fontWeight: 'bold'
                }
            );
            closeButton.setOrigin(0.5);
            closeButton.setInteractive();
            
            // 添加提示文字
            const hintText = this.add.text(
                this.cameras.main.centerX,
                this.cameras.main.centerY + 30,
                '按空格键继续',
                {
                    fontSize: '14px',
                    fill: '#cccccc',
                    fontFamily: 'Arial, sans-serif'
                }
            );
            hintText.setOrigin(0.5);
            
            // 保存消息元素
            this.messageElements = {
                bg: dialogBg,
                text: dialogText,
                button: closeButton,
                hint: hintText,
                buttonBg: buttonBg
            };
            
            // 保存回调
            this.messageCallback = onClose;
            
            // 禁用菜单选择
            this.menuActive = false;
            
            // 设置关闭事件
            closeButton.on('pointerdown', () => {
                this.closeMessage();
            });
            
            // 添加键盘关闭
            this.input.keyboard.once('keydown-SPACE', () => {
                this.closeMessage();
            });
        } catch (error) {
            DebugHelper.error('showMessage error:', error);
        }
    }
    
    closeMessage() {
        try {
            if (this.messageElements) {
                this.messageElements.bg.destroy();
                if (this.messageElements.buttonBg) this.messageElements.buttonBg.destroy();
                if (this.messageElements.hint) this.messageElements.hint.destroy();
                this.messageElements.text.destroy();
                this.messageElements.button.destroy();
                this.messageElements = null;
            }
            
            // 调用回调（如果有）
            if (this.messageCallback) {
                this.messageCallback();
                this.messageCallback = null;
            }
            
            // 重新启用菜单
            this.menuActive = true;
        } catch (error) {
            DebugHelper.error('closeMessage error:', error);
        }
    }
    
    update(time, delta) {
        // 更新星空背景
        this.background.updateStarBackground(time);
    }

    shutdown() {
        if (this.inputHandler) {
            this.inputHandler.destroyControls();
        }
    }
}
